import { IFilterControl } from '../../../../components/protected/Dialogs/components/FilterSetupPlaceholder/FilterSetupPlaceholder';
import { IStore } from '../../../../services/LocalStorage/IStore';

export interface FilterParam {
  name: string;
  type: string;
  value: string;
  negation?: boolean;
  includeNull?: boolean;
}

const OPERATOR_TO_FILTER_TYPE: Record<string, string> = {
  equals: 'in',
  not_equals: 'in',
  contains: 'contains',
  does_not_contain: 'contains',
  begins_with: 'begins_with',
  ends_with: 'ends_with',
  greater_than: 'greater',
  less_than: 'less',
  between: 'between',
  before: 'less',
  after: 'greater',
  relative_preset: 'relative_preset',
  is_true: 'is_true',
  is_false: 'is_false',
  is_empty: 'is_empty',
  is_not_empty: 'is_not_empty',
};

const NEGATIVE_OPERATORS: string[] = [
  'not_equals',
  'does_not_contain',
  'is_false',
  'is_not_empty',
];

const NULLABLE_OPERATORS: string[] = [
  'is_empty',
];
/**
 * Converts an IFilterControl to a FilterParam
 * @param filter The IFilterControl to convert
 * @param store The IStore instance to access runtime userId when isCurrentUser flag is set
 * @returns FilterParam or null if conversion is not possible
 */
export function convertFilterControlToFilterParam(filter: IFilterControl, store: IStore): FilterParam | null {
  if (!filter || !filter.filterInfo || filter.filterInfo.length === 0) {
    return null;
  }

  const filterItem = filter.filterInfo[0];
  if (!filterItem) {
    return null;
  }

  // Map dataType to filter name
  let filterName = filter.name;
  
  // If filter name contains '|', split and use the first part
  if (filterName && filterName.includes('|')) {
    const parts = filterName.split('|');
    var index:number = ['dimension1', 'dimension2', 'dimension3'].includes(parts[0]) ? 1 : 0;
    filterName = parts[index]?.trim() || filterName;
  }

  // Map operator to filter type
  // Convert is_empty/is_not_empty to API format: type='empty' with negation
  let filterType: string;
  let negation = false;
  
  if (filterItem.type === 'is_empty') {
    filterType = 'empty';
    negation = false;
  } else if (filterItem.type === 'is_not_empty') {
    filterType = 'empty';
    negation = true;
  } else {
    filterType = OPERATOR_TO_FILTER_TYPE[filterItem.type] || filterItem.type || 'in';
    negation = NEGATIVE_OPERATORS.includes(filterItem.type);
  }

  // Extract value - use runtime userId if isCurrentUser flag is set
  let filterValue = filterItem.value || '';
  if (filterItem.isCurrentUser === true && store) {
    filterValue = String(store.UserId);
  }

  const result: FilterParam = {
    name: filterName,
    type: filterType,
    value: filterValue,
    negation: negation,
  };

  // Only include includeNull for is_empty (before conversion)
  if (NULLABLE_OPERATORS.includes(filterItem.type)) {
    result.includeNull = true;
  }

  return result;
}

/**
 * Syncs filterParams from filterControls by converting all IFilterControl[] to FILTER.COL entries
 * @param filterControls Array of IFilterControl objects
 * @param filterParams Current filterParams object
 * @param store The IStore instance to access runtime userId when isCurrentUser flag is set
 * @returns Updated filterParams with FILTER.COL synced from filterControls
 */
export function syncFilterParamsFromControls(
  filterControls: IFilterControl[],
  filterParams: any,
  store: IStore
): any {
  if (!filterParams || !filterParams.FILTER || !filterParams.FILTER.COL) {
    return filterParams;
  }

  // Get filter names that are managed by filterControls
  const managedFilterNames = new Set<string>();
  filterControls.forEach((control) => {
    const param = convertFilterControlToFilterParam(control, store);
    if (param) {
      managedFilterNames.add(param.name);
    }
  });

  // Start with existing filterParams
  const updatedFilterParams = { ...filterParams };
  updatedFilterParams.FILTER = { ...filterParams.FILTER };
  updatedFilterParams.FILTER.COL = [...filterParams.FILTER.COL];

  // Remove entries that are managed by filterControls
  updatedFilterParams.FILTER.COL = updatedFilterParams.FILTER.COL.filter(
    (col: FilterParam) => !managedFilterNames.has(col.name)
  );

  // Add entries from filterControls
  filterControls.forEach((control) => {
    const param = convertFilterControlToFilterParam(control, store);
    if (param) {
      // Allow empty values for 'empty' type filters (is_empty/is_not_empty)
      const isEmptyType = param.type === 'empty';
      if (isEmptyType || param.value !== '') {
        // Find existing entry with same name
        const existingIndex = updatedFilterParams.FILTER.COL.findIndex(
          (col: FilterParam) => col.name === param.name
        );

        if (existingIndex !== -1) {
          // Update existing entry
          updatedFilterParams.FILTER.COL[existingIndex] = param;
        } else {
          // Add new entry
          updatedFilterParams.FILTER.COL.push(param);
        }
      }
    }
  });

  // Remove empty values (but preserve 'empty' type filters which have empty values)
  updatedFilterParams.FILTER.COL = updatedFilterParams.FILTER.COL.filter(
    (col: FilterParam) => col.type === 'empty' || (col.value !== '' && col.value !== null && col.value !== undefined)
  );

  return updatedFilterParams;
}

/**
 * Converts a FilterParam back to IFilterItem format for updating filterInfo
 * @param filterParam The FilterParam to convert
 * @returns IFilterItem or null if conversion is not possible
 */
export function convertFilterParamToFilterItem(filterParam: FilterParam): { type: string; value: string; negation?: boolean; includeNull?: boolean } | null {
  if (!filterParam) {
    return null;
  }

  // Map filter type back to operator
  // Handle 'empty' type with negation - convert back to is_empty/is_not_empty
  let operator: string;
  let shouldIncludeNegation = false;
  
  if (filterParam.type === 'empty') {
    operator = filterParam.negation ? 'is_not_empty' : 'is_empty';
    // is_empty/is_not_empty don't need negation flag since operator indicates negation
  } else {
    // Handle operators that use negation flag
    if (filterParam.type === 'in' && filterParam.negation) {
      operator = 'not_equals';
    } else if (filterParam.type === 'contains' && filterParam.negation) {
      operator = 'does_not_contain';
    } else {
      const operatorMap: Record<string, string> = {
        in: 'equals',
        not_in: 'not_equals',
        contains: 'contains',
        does_not_contain: 'does_not_contain',
        begins_with: 'begins_with',
        ends_with: 'ends_with',
        greater_than: 'greater',
        less_than: 'less',
        between: 'between',
        before: 'before',
        after: 'after',
        relative_preset: 'relative_preset',
        is_true: 'is_true',
        is_false: 'is_false',
        is_empty: 'is_empty',
        is_not_empty: 'is_not_empty',
      };
      operator = operatorMap[filterParam.type] || filterParam.type || 'equals';
      // For other types, include negation if present
      shouldIncludeNegation = filterParam.negation !== undefined;
    }
  }

  const value = filterParam.value || '';

  const result: { type: string; value: string; negation?: boolean; includeNull?: boolean } = {
    type: operator,
    value: value,
  };

  // Include negation if needed (for operators that aren't converted to negative forms)
  if (shouldIncludeNegation && filterParam.negation !== undefined) {
    result.negation = filterParam.negation;
  }

  // Include includeNull if present
  if (filterParam.includeNull !== undefined) {
    result.includeNull = filterParam.includeNull;
  }

  return result;
}


